import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import OfferWatcher from "@/components/OfferWatcher";
import AvailabilityReminder from "@/components/AvailabilityReminder";
import WaitingWatcher from "@/components/WaitingWatcher";
import RequireAuth from "@/components/RequireAuth";
import AuthCallback from "@/pages/AuthCallback";
import GoogleCallback from "@/pages/GoogleCallback";
import Home from "@/pages/Home";
import ServicePage from "@/pages/ServicePage";
import NewRequest from "@/pages/NewRequest";
import RequestStatus from "@/pages/RequestStatus";
import MyRequests from "@/pages/MyRequests";
import ProviderPublic from "@/pages/ProviderPublic";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import ChangePassword from "@/pages/ChangePassword";
import Account from "@/pages/Account";
import ProviderForm from "@/pages/ProviderForm";
import ProPlan from "@/pages/ProPlan";
import Admin from "@/pages/Admin";
import Notifications from "@/pages/Notifications";
import SeoRedirect from "@/pages/SeoRedirect";
import { Terms, Privacy } from "@/pages/Legal";

function ForcePasswordChange() {
  const { user } = useAuth();
  const location = useLocation();
  if (user?.must_change_password && location.pathname !== "/trocar-senha") {
    return <Navigate to="/trocar-senha" replace />;
  }
  return null;
}

let lastGaPath = null;
function useGaPageViews(location) {
  useEffect(() => {
    const path = window.location.pathname + window.location.search;
    if (path === lastGaPath) return;
    const firstLoad = lastGaPath === null;
    lastGaPath = path;
    // Meta Pixel: first PageView comes from the base code in index.html; route changes send their own.
    if (!firstLoad && typeof window.fbq === "function") window.fbq("track", "PageView");
    if (typeof window.gtag === "function") {
      window.gtag("event", "page_view", {
        page_path: path,
        page_title: document.title,
      });
    }
  }, [location.pathname, location.search]);
}

function AppRouter() {
  const location = useLocation();
  useGaPageViews(location);
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  const auth = (el) => <RequireAuth>{el}</RequireAuth>;
  return (
    <div className="flex min-h-screen flex-col bg-white">
      <Header />
      <ForcePasswordChange />
      <OfferWatcher />
      <AvailabilityReminder />
      <WaitingWatcher />
      <main className="flex-1">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/servico/:slug" element={<ServicePage />} />
          <Route path="/prestador/:id" element={<ProviderPublic />} />
          <Route path="/entrar" element={<Login />} />
          <Route path="/cadastro" element={<Register />} />
          <Route path="/esqueci-senha" element={<ForgotPassword />} />
          <Route path="/redefinir-senha" element={<ResetPassword />} />
          <Route path="/auth/google" element={<GoogleCallback />} />
          <Route path="/termos" element={<Terms />} />
          <Route path="/privacidade" element={<Privacy />} />
          <Route path="/trocar-senha" element={auth(<ChangePassword />)} />
          <Route path="/pedido/novo" element={auth(<NewRequest />)} />
          <Route path="/pedido/:id" element={auth(<RequestStatus />)} />
          <Route path="/meus-pedidos" element={auth(<MyRequests />)} />
          <Route path="/perfil" element={auth(<Account />)} />
          <Route path="/perfil/prestador" element={auth(<ProviderForm />)} />
          <Route path="/plano-pro" element={auth(<ProPlan />)} />
          <Route path="/admin" element={auth(<Admin />)} />
          <Route path="/notificacoes" element={auth(<Notifications />)} />
          <Route path="/:slug" element={<SeoRedirect />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      <Footer />
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster position="top-center" richColors toastOptions={{ style: { fontSize: "17px" } }} />
      </AuthProvider>
    </BrowserRouter>
  );
}
